/**
 * DashboardPage Component
 * Main dashboard after login
 */

import { useState, useEffect, useMemo } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import { membersService } from '../services/members.service';
import { dashboardService } from '../services/dashboard.service';
import { importsService } from '../services/imports.service';
import { StatCard } from '../components/dashboard/StatCard';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MemberFormModal } from '../components/members/MemberFormModal';
import { TeamFormModal } from '../components/teams/TeamFormModal';
import type { Member } from '../types';

interface AnniversaryMember {
  member: Member;
  anniversaryDate: Date;
}

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [memberImages, setMemberImages] = useState<Map<number, string>>(new Map());
  const [overviewImages, setOverviewImages] = useState<Map<number, string>>(new Map());

  // Modal states for Quick Links
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
  const [isTeamFormOpen, setIsTeamFormOpen] = useState(false);

  // Fetch dashboard statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: dashboardService.getStats,
    enabled: hasAdminAccess(user?.role),
  });

  // Fetch dashboard overview data
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboardOverview'],
    queryFn: dashboardService.getOverview,
    enabled: hasAdminAccess(user?.role),
  });

  // Fetch members data (only for admins - for anniversary reminders)
  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
    enabled: hasAdminAccess(user?.role),
  });

  // Members missing a join date (Import Data feature widget)
  const { data: missingJoin } = useQuery({
    queryKey: ['missingJoinDate'],
    queryFn: importsService.missingJoinDate,
    enabled: hasAdminAccess(user?.role),
  });

  // Calculate upcoming anniversaries
  const anniversaryData = useMemo(() => {
    if (!hasAdminAccess(user?.role) || members.length === 0) {
      return {
        sixMonths: [],
        oneYear: [],
        fiveYears: [],
        tenYears: [],
        fifteenYears: [],
        twentyYears: [],
      };
    }

    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);

    const milestones = [
      { key: 'sixMonths', months: 6, label: '6 months' },
      { key: 'oneYear', months: 12, label: '1 year' },
      { key: 'fiveYears', months: 60, label: '5 years' },
      { key: 'tenYears', months: 120, label: '10 years' },
      { key: 'fifteenYears', months: 180, label: '15 years' },
      { key: 'twentyYears', months: 240, label: '20 years' },
    ];

    const result: Record<string, AnniversaryMember[]> = {
      sixMonths: [],
      oneYear: [],
      fiveYears: [],
      tenYears: [],
      fifteenYears: [],
      twentyYears: [],
    };

    members.forEach((m) => {
      if (!m.date_joined) return;

      const joinedDate = new Date(m.date_joined);

      // Skip members with future join dates (not yet joined)
      if (joinedDate > today) return;

      milestones.forEach((milestone) => {
        // Calculate the anniversary date for this milestone
        const anniversaryDate = new Date(joinedDate);
        anniversaryDate.setMonth(anniversaryDate.getMonth() + milestone.months);

        // Check if anniversary is within the next 60 days
        if (anniversaryDate >= today && anniversaryDate <= sixtyDaysFromNow) {
          result[milestone.key].push({
            member: m,
            anniversaryDate,
          });
        }
      });
    });

    // Sort each list by anniversary date
    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => a.anniversaryDate.getTime() - b.anniversaryDate.getTime());
    });

    return result;
  }, [members, user?.role]);

  // Load member images
  useEffect(() => {
    if (!hasAdminAccess(user?.role)) return;

    const loadImages = async () => {
      const allMembers = [
        ...anniversaryData.sixMonths,
        ...anniversaryData.oneYear,
        ...anniversaryData.fiveYears,
        ...anniversaryData.tenYears,
        ...anniversaryData.fifteenYears,
        ...anniversaryData.twentyYears,
      ].map((am) => am.member);

      // Get unique members
      const uniqueMembers = Array.from(new Map(allMembers.map((m) => [m.id, m])).values());

      for (const m of uniqueMembers) {
        if (m.has_profile_picture && !memberImages.has(m.id)) {
          try {
            const imageUrl = await membersService.fetchMemberImage(m.id);
            if (imageUrl) {
              setMemberImages((prev) => new Map(prev).set(m.id, imageUrl));
            }
          } catch (error) {
            console.error(`Failed to load image for member ${m.id}`);
          }
        }
      }
    };

    loadImages();
    // Note: No cleanup needed - imageCache in membersService manages blob URLs globally
  }, [anniversaryData, user?.role]);

  // Load images for overview section members
  useEffect(() => {
    if (!hasAdminAccess(user?.role) || !overview) return;

    const loadImages = async () => {
      const allOverviewMembers = [
        ...overview.recentlyModified,
        ...overview.upcomingBirthdays,
        // photosMissing members don't have pictures by definition
      ];

      // Get unique members
      const uniqueMembers = Array.from(
        new Map(allOverviewMembers.map((m) => [m.id, m])).values()
      );

      for (const m of uniqueMembers) {
        if (m.has_profile_picture && !overviewImages.has(m.id)) {
          try {
            const imageUrl = await membersService.fetchMemberImage(m.id);
            if (imageUrl) {
              setOverviewImages((prev) => new Map(prev).set(m.id, imageUrl));
            }
          } catch (error) {
            console.error(`Failed to load image for member ${m.id}`);
          }
        }
      }
    };

    loadImages();
    // Note: No cleanup needed - imageCache in membersService manages blob URLs globally
  }, [overview, user?.role]);

  // Format date as "DD-MMM-YYYY"
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date as "MMM DD" for birthdays
  const formatBirthday = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  return (
    <Layout>
      {hasAdminAccess(user?.role) && (
        <div className="space-y-8">
          {/* Quick Links Section */}
          <div>
            <div className="flex flex-wrap gap-8">
              <Button
                onClick={() => setIsMemberFormOpen(true)}
                className="shrink-0 w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Add Member
              </Button>
              <Button
                onClick={() => navigate('/import')}
                className="shrink-0 w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Import Data
              </Button>
              <Button
                onClick={() => navigate('/users')}
                className="shrink-0 w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Manage Users
              </Button>
              <div className="hidden">
                <Button
                  onClick={() => {}}
                  className="shrink-0 w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-orange-600 to-orange-700 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Manage Groups
                </Button>
              </div>
            </div>
          </div>

          {/* Statistics Counters Section */}
          <div>
            <h2 className="text-2xl font-bold text-heading mb-6">Dashboard Statistics</h2>
            {statsLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <StatCard
                  label="Total Members"
                  value={stats.totalMembers}
                  linkTo="/members"
                  icon={
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                  change={{ value: stats.totalMembersChange, type: 'count' }}
                />
                <StatCard
                  label="Total Teams"
                  value={stats.totalTeams}
                  linkTo="/teams"
                  icon={
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                  change={{ value: stats.totalTeamsChange, type: 'count' }}
                />
                <StatCard
                  label="App Logins"
                  value={stats.appLogins.thisWeek}
                  icon={
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m10 17 5-5-5-5" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H3" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    </svg>
                  }
                  change={{ value: stats.appLogins.changePercent, type: 'percent' }}
                />
                <StatCard
                  label="Completed Quizzes"
                  value={stats.completedQuizzes.thisWeek}
                  icon={
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  }
                  change={{ value: stats.completedQuizzes.changePercent, type: 'percent' }}
                />
              </div>
            ) : null}
          </div>

          {/* Overview Section */}
          <div>
            <h2 className="text-2xl font-bold text-heading mb-6">Overview</h2>
            {overviewLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : overview ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Recently Modified */}
                <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
                  <h3 className="text-lg font-semibold text-heading mb-4">Recently Modified</h3>
                  <div>
                    {/* Header Row - Fixed */}
                    <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                      <div className="text-sm text-meta">Name</div>
                      <div className="text-sm text-meta">Updated</div>
                    </div>

                    {/* Scrollable Member List */}
                    {overview.recentlyModified.length === 0 ? (
                      <p className="text-sm text-meta py-4 text-center">No recent updates</p>
                    ) : (
                      <div className="max-h-[310px] overflow-y-auto">
                        <div className="space-y-2 pt-2 pr-3">
                          {overview.recentlyModified.map((m) => (
                            <div key={m.id} className="flex items-center justify-between py-2">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  {overviewImages.has(m.id) ? (
                                    <img
                                      src={overviewImages.get(m.id)}
                                      alt={`${m.first_name} ${m.last_name}`}
                                      className="h-8 w-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                      <span className="text-xs font-medium text-white">
                                        {m.first_name[0]}{m.last_name[0]}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <Link
                                  to={`/members/${m.id}`}
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                                >
                                  {m.first_name} {m.last_name}
                                </Link>
                              </div>
                              <div className="text-sm text-meta ml-4 flex-shrink-0">
                                {new Date(m.updated_at).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upcoming Birthdays */}
                <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
                  <h3 className="text-lg font-semibold text-heading mb-4">Upcoming Birthdays</h3>
                  <div>
                    {/* Header Row - Fixed */}
                    <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                      <div className="text-sm text-meta">Name</div>
                      <div className="text-sm text-meta">Birthday</div>
                    </div>

                    {/* Scrollable Member List */}
                    {overview.upcomingBirthdays.length === 0 ? (
                      <p className="text-sm text-meta py-4 text-center">No upcoming birthdays</p>
                    ) : (
                      <div className="max-h-[310px] overflow-y-auto">
                        <div className="space-y-2 pt-2 pr-3">
                          {overview.upcomingBirthdays.map((m) => (
                            <div key={m.id} className="flex items-center justify-between py-2">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  {overviewImages.has(m.id) ? (
                                    <img
                                      src={overviewImages.get(m.id)}
                                      alt={`${m.first_name} ${m.last_name}`}
                                      className="h-8 w-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                      <span className="text-xs font-medium text-white">
                                        {m.first_name[0]}{m.last_name[0]}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <Link
                                  to={`/members/${m.id}`}
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                                >
                                  {m.first_name} {m.last_name}
                                </Link>
                              </div>
                              <div className="text-sm text-heading ml-4 flex-shrink-0">
                                {formatBirthday(m.date_of_birth)}
                                {m.days_until === 0 ? ' (Today!)' : m.days_until === 1 ? ' (Tomorrow)' : ` (${m.days_until}d)`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photos Missing */}
                <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
                  <h3 className="text-lg font-semibold text-heading mb-4">Photos Missing</h3>
                  <div>
                    {/* Header Row - Fixed */}
                    <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                      <div className="text-sm text-meta">Name</div>
                      <div className="text-sm text-meta">Date Joined</div>
                    </div>

                    {/* Scrollable Member List */}
                    {overview.photosMissing.length === 0 ? (
                      <p className="text-sm text-meta py-4 text-center">
                        {stats?.totalMembers === 0 ? 'No members added yet' : 'All members have photos!'}
                      </p>
                    ) : (
                      <div className="max-h-[310px] overflow-y-auto">
                        <div className="space-y-2 pt-2 pr-3">
                          {overview.photosMissing.map((m) => (
                            <div key={m.id} className="flex items-center justify-between py-2">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                                    <span className="text-xs font-medium text-white">
                                      {m.first_name[0]}{m.last_name[0]}
                                    </span>
                                  </div>
                                </div>
                                <Link
                                  to={`/members/${m.id}`}
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                                >
                                  {m.first_name} {m.last_name}
                                </Link>
                              </div>
                              <div className="text-sm text-meta ml-4 flex-shrink-0">
                                {m.date_joined ? formatDate(new Date(m.date_joined)) : 'N/A'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Missing date joined */}
                <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
                  <h3 className="text-lg font-semibold text-heading mb-4">
                    Missing date joined{missingJoin && missingJoin.count > 0 ? ` (${missingJoin.count})` : ''}
                  </h3>
                  {!missingJoin || missingJoin.count === 0 ? (
                    <p className="text-sm text-meta py-4 text-center">All members have a join date.</p>
                  ) : (
                    <div className="max-h-[310px] overflow-y-auto">
                      <div className="space-y-2 pt-2 pr-3">
                        {missingJoin.members.map((m) => (
                          <div key={m.id} className="flex items-center justify-between py-2">
                            <Link to={`/members/${m.id}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate">
                              {m.first_name} {m.last_name}
                            </Link>
                            <Link to={`/members/${m.id}`} className="text-xs text-meta hover:text-heading ml-4 flex-shrink-0">Fix</Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Anniversary Reminders */}
          <div>
            <h2 className="text-2xl font-bold text-heading mb-6">Anniversary Reminders</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* 6 Months Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 6 months in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.sixMonths.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.sixMonths.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {/* Profile Picture */}
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Name Link */}
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          {/* Date */}
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 1 Year Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 1 year in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.oneYear.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.oneYear.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 5 Years Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 5 years in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.fiveYears.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.fiveYears.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 10 Years Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 10 years in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.tenYears.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.tenYears.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 15 Years Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 15 years in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.fifteenYears.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.fifteenYears.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 20 Years Card */}
            <div className="bg-surface rounded-lg shadow border border-[var(--border-default)] p-6">
              <h3 className="text-lg font-semibold text-heading mb-4">
                Reminder for 20 years in organization
              </h3>

              <div>
                {/* Header Row - Fixed */}
                <div className="flex items-center justify-between pb-2 border-b border-[var(--border-default)] pr-3">
                  <div className="text-sm text-meta">Name</div>
                  <div className="text-sm text-meta">Date</div>
                </div>

                {/* Scrollable Member List */}
                {anniversaryData.twentyYears.length === 0 ? (
                  <p className="text-sm text-meta py-4 text-center">No upcoming anniversaries</p>
                ) : (
                  <div className="max-h-[310px] overflow-y-auto">
                    <div className="space-y-2 pt-2 pr-3">
                      {anniversaryData.twentyYears.map(({ member: m, anniversaryDate }) => (
                        <div key={m.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {memberImages.has(m.id) ? (
                                <img
                                  src={memberImages.get(m.id)}
                                  alt={`${m.first_name} ${m.last_name}`}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white">
                                    {m.first_name[0]}{m.last_name[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/members/${m.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {m.first_name} {m.last_name}
                            </Link>
                          </div>
                          <div className="text-sm text-heading ml-4 flex-shrink-0">
                            {formatDate(anniversaryDate)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Modals for Quick Links */}
      <MemberFormModal
        isOpen={isMemberFormOpen}
        mode="add"
        onClose={() => setIsMemberFormOpen(false)}
      />

      <TeamFormModal
        isOpen={isTeamFormOpen}
        mode="add"
        onClose={() => setIsTeamFormOpen(false)}
      />
    </Layout>
  );
};
